import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserMobileNumberDetailResponse } from "../../deposit/models/response";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import {
  WithdrawType,
  WithdrawTypeListResponseList,
} from "../../withdraw/models";
import CreateWithdrawRequest from "./CreateWithdrawRequest";
import {
  Button,
  Card,
  CardBody,
  SnapCarousel,
} from "../../../components/ui";
import localization from "../../../config/localization";
import depositv1Service from "../../deposit/services/deposit.service";
import { localStorageService } from "../../../services/localStorageService";
import CreateABCCryptoWithdrawRequest from "./CreateABCCryptoWithdrawRequest";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import userDeviceStore from "../../../store/device.store";
import { DeviceType } from "../../../models/constants/deviceType";
import { Modal } from "../../../components/ui/modals";

interface WithdrawV2Props {
  withdrawFlow: string;
  withdrawTypes: WithdrawTypeListResponseList[];
  username: string | null;
}

export default function WithdrawV2({
  withdrawFlow,
  withdrawTypes,
  username,
}: WithdrawV2Props) {
  const navigate = useNavigate();
  const [selectedWithdrawType, setSelectedWithdrawType] =
    useState<WithdrawTypeListResponseList | null>(null);
  const [userMobileDetail, setUserMobileDetail] =
    useState<UserMobileNumberDetailResponse | null>(null);
  const [showVerifyMobileModal, setShowVerifyMobileModal] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const deviceType = userDeviceStore((state) => state.deviceType);

  const usernameRef = useRef<string | null>("User");

  const [itemWidth, setItemWidth] = useState("100%");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      if (width >= 1024) {
        setItemWidth("20vw");
      } else if (width >= 640) {
        setItemWidth("70vw");
      } else {
        setItemWidth("82vw");
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const init = async () => {
    const response = await depositv1Service.getUserMobileDetail();
    if (response) {
      setUserMobileDetail(response);
      if (!userMobileDetail?.ismobileverified) {
        usernameRef.current = localStorageService.getItem<string>("username");
      }
    }
    dispatch(changeLoaderState(false));
  };

  const onWithdrawTypeClick = async (item: WithdrawTypeListResponseList) => {
    if (
      item.name?.toLowerCase() === WithdrawType.ABCCrypto &&
      !userMobileDetail?.ismobileverified
    ) {
      setShowVerifyMobileModal(true);
      return;
    }
    setSelectedWithdrawType(item);
  };

  const handleVerifyProceed = () => {
    navigate("/profile");
  };

  if (selectedWithdrawType) {
    if (selectedWithdrawType.name?.toLowerCase() === WithdrawType.ABCCrypto) {
      return (
        <CreateABCCryptoWithdrawRequest
          selectedWithdrawType={selectedWithdrawType}
          withdrawFlow={withdrawFlow}
          onBack={() => setSelectedWithdrawType(null)}
        />
      );
    }
    return (
      <CreateWithdrawRequest
        selectedWithdrawType={selectedWithdrawType}
        withdrawFlow={withdrawFlow}
        username={username}
        onBack={() => setSelectedWithdrawType(null)}
      />
    );
  }

  const content = (
    <div className="p-4">
      <div className="mt-10">
        <SnapCarousel
          infinite={true}
          startIndex={0}
          dotsVariant="centeredCounter"
          showAdjacent={true}
          gap={isMobile ? "gap-4" : "gap-2"}
          itemWidth={itemWidth}
        >
          {withdrawTypes?.map((withdrawType) => (
            <div key={withdrawType.id} className={isMobile ? "" : "px-2"}>
              {isMobile ? (
                <div className="relative mx-auto h-[333px] w-full max-w-[255px] rounded-[14px] border-2 border-[#2C2C2C] bg-black-gradient px-4 pb-[22px] pt-8 text-white">
                  <div className="flex h-full flex-col items-center">
                    <div className="mt-2 flex h-[88px] w-[88px] items-center justify-center rounded-2xl bg-white">
                      <img
                        src={`${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawType}/${withdrawType.logoUrl}`}
                        alt={withdrawType.name}
                        className="h-14 w-14 object-contain"
                      />
                    </div>

                    <p className="mt-4 text-center text-[14px] font-semibold leading-6">
                      {withdrawType.accountName}
                    </p>

                    <div className="mt-4 flex justify-center">
                      <div className="flex items-center gap-1.5 rounded-full border border-[#3B3B3B] bg-black px-5 py-2.5">
                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          {localization.min.replace(" ₹", "")}
                        </span>

                        <span className="text-[15px] font-medium text-white">
                          {"\u20B9"}
                          {withdrawType.minimumAmount}
                        </span>

                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          -
                        </span>

                        <span className="text-[14px] font-normal text-[#C7C7CC]">
                          {localization.max.replace(" ₹", "")}
                        </span>

                        <span className="text-[15px] font-medium text-white">
                          {"\u20B9"}
                          {withdrawType.maximumAmount}
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto w-full">
                      <Button
                        onClick={() => onWithdrawTypeClick(withdrawType)}
                        className="w-full rounded-md bg-secondary-gradient py-[10px] text-sm font-semibold"
                      >
                        {localization.select}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative w-full max-w-[260px] mx-auto rounded-2xl bg-black-gradient border-2 border-[#2C2C2C] px-4 py-5 text-white">
                  <div className="flex items-center justify-center mt-6">
                    <img
                      src={`${BLOB_IMAGE_URL}${BlobContainerConsts.WithdrawType}/${withdrawType.logoUrl}`}
                      alt={withdrawType.name}
                      className="object-contain h-24 w-24"
                    />
                  </div>

                  <p className="mt-2 text-center font-bold text-sm leading-[24px]">
                    {withdrawType.accountName}
                  </p>

                  <div className="flex justify-center mt-5">
                    <div className="px-4 py-2 rounded-full border border-[#3B3B3B] bg-black flex items-center gap-1">
                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        {localization.min.replace(" ₹", "")}
                      </span>

                      <span className="text-white text-[14px] font-medium">
                        {"\u20B9"}
                        {withdrawType.minimumAmount}
                      </span>

                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        -
                      </span>

                      <span className="text-[#C7C7CC] text-[13px] font-normal">
                        {localization.max.replace(" ₹", "")}
                      </span>

                      <span className="text-white text-[14px] font-medium">
                        {"\u20B9"}
                        {withdrawType.maximumAmount}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => onWithdrawTypeClick(withdrawType)}
                    className="mt-5 w-full rounded-md bg-secondary-gradient py-[10px] text-sm font-semibold"
                  >
                    {localization.select}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </SnapCarousel>
      </div>

      {showVerifyMobileModal && (
        <Modal isOpen={showVerifyMobileModal} onClose={handleVerifyProceed}>
          <p className="mt-3 text-[18px] leading-[28px] text-gray-600">
            <span className="mr-2">{localization.hi_lbl}</span>
            <span className="font-bold text-black">{usernameRef.current}</span>
          </p>

          <p className="mt-2 px-4 text-[18px] font-semibold leading-[28px] text-gray-600">
            {localization.withdraw_mobile_verify_note}
          </p>

          <Button
            onClick={handleVerifyProceed}
            className="mt-6 w-full rounded-lg bg-[#111111] py-3.5 font-semibold text-white hover:bg-[#EDEAEA] hover:text-black transition-all duration-200"
          >
            {localization.proceed}
          </Button>
        </Modal>
      )}
    </div>
  );

  return deviceType !== DeviceType.Web ? (
    content
  ) : (
    <Card>
      <CardBody showGradient>
        <div className="w-full mx-auto">
          <p className="pt-2 text-[22px] font-semibold text-center">
            {localization.withdraw_top_lbl}
          </p>
          {content}
        </div>
      </CardBody>
    </Card>
  );
}
